import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, TrendingDown, DollarSign, Target, AlertCircle } from 'lucide-react';

export default function ROICalculator({ event }) {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPercent = (value) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  // ROI Calculations
  const actualRevenue = event.total_revenue || 0;
  const projectedRevenue = event.projected_revenue || 0;
  const totalExpenses = event.total_expenses || 0;
  const boothCost = event.booth_cost || 0;
  
  // Total Investment = Booth + All Expenses
  const totalInvestment = boothCost + totalExpenses;
  
  // Net Profit
  const netProfit = actualRevenue - totalInvestment;
  
  // ROI = (Net Profit / Total Investment) * 100
  const roi = totalInvestment > 0 ? (netProfit / totalInvestment) * 100 : 0;
  
  // Revenue Accuracy (Projected vs Actual)
  const revenueVariance = projectedRevenue > 0 
    ? ((actualRevenue - projectedRevenue) / projectedRevenue) * 100 
    : 0;
  
  // Profit Margin
  const profitMargin = actualRevenue > 0 ? (netProfit / actualRevenue) * 100 : 0;
  
  // Break-even point
  const breakEvenRevenue = totalInvestment;
  const revenueNeededToBreakEven = Math.max(0, breakEvenRevenue - actualRevenue);
  
  // Worth attending?
  const isWorthIt = roi > 0 && netProfit > 0;
  const shouldConsider = roi >= 50; // At least 50% return

  return (
    <div className="space-y-6">
      {/* Main ROI Card */}
      <Card 
        className={`bg-card border-2 ${
          isWorthIt ? 'border-green-500/50' : 'border-red-500/50'
        }`}
        data-testid="roi-calculator-card"
      >
        <CardHeader>
          <CardTitle className="text-2xl font-heading flex items-center gap-2">
            <DollarSign className="h-6 w-6 text-pink-500" />
            Convention ROI Analysis
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Investment Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-secondary/30 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                Booth Cost
              </p>
              <p className="text-2xl font-bold font-mono" data-testid="roi-booth-cost">
                {formatCurrency(boothCost)}
              </p>
            </div>
            <div className="bg-secondary/30 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                Operating Expenses
              </p>
              <p className="text-2xl font-bold font-mono" data-testid="roi-expenses">
                {formatCurrency(totalExpenses)}
              </p>
            </div>
            <div className="bg-secondary/30 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                Total Investment
              </p>
              <p className="text-2xl font-bold font-mono text-red-400" data-testid="roi-total-investment">
                {formatCurrency(totalInvestment)}
              </p>
            </div>
          </div>

          {/* Main ROI Metric */}
          <div className="bg-gradient-to-br from-pink-500/20 to-pink-500/5 border border-pink-500/50 p-6 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground uppercase tracking-wider mb-2">
                  Return on Investment (ROI)
                </p>
                <div className="flex items-center gap-3">
                  <p 
                    className={`text-5xl font-bold font-mono ${
                      roi >= 0 ? 'text-green-400' : 'text-red-400'
                    }`}
                    data-testid="roi-percentage"
                  >
                    {formatPercent(roi)}
                  </p>
                  {roi >= 0 ? (
                    <TrendingUp className="h-8 w-8 text-green-400" />
                  ) : (
                    <TrendingDown className="h-8 w-8 text-red-400" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Net Profit: <span className={`font-bold ${netProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {formatCurrency(netProfit)}
                  </span>
                </p>
              </div>
              <div className="text-right">
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
                  isWorthIt ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {isWorthIt ? (
                    <>
                      <TrendingUp className="h-4 w-4" />
                      <span className="font-semibold">Profitable</span>
                    </>
                  ) : (
                    <>
                      <TrendingDown className="h-4 w-4" />
                      <span className="font-semibold">Loss</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Additional Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="bg-secondary/30 border-border/50">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                      Profit Margin
                    </p>
                    <p className={`text-2xl font-bold font-mono ${
                      profitMargin >= 0 ? 'text-green-400' : 'text-red-400'
                    }`} data-testid="roi-profit-margin">
                      {formatPercent(profitMargin)}
                    </p>
                  </div>
                  <Target className="h-5 w-5 text-pink-500" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Profit as % of revenue
                </p>
              </CardContent>
            </Card>

            <Card className="bg-secondary/30 border-border/50">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground uppercase tracking-wider mb-1">
                      Revenue Accuracy
                    </p>
                    <p className={`text-2xl font-bold font-mono ${
                      Math.abs(revenueVariance) <= 10 ? 'text-green-400' : 
                      Math.abs(revenueVariance) <= 25 ? 'text-yellow-400' : 'text-red-400'
                    }`} data-testid="roi-revenue-variance">
                      {formatPercent(revenueVariance)}
                    </p>
                  </div>
                  <AlertCircle className="h-5 w-5 text-pink-500" />
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  {projectedRevenue > 0 
                    ? `Projected: ${formatCurrency(projectedRevenue)}`
                    : 'No projection set'
                  }
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Break-even Analysis */}
          {actualRevenue < breakEvenRevenue && (
            <div className="bg-yellow-500/10 border border-yellow-500/50 p-4 rounded-lg">
              <p className="text-sm font-semibold text-yellow-400 mb-1">
                Break-Even Point Not Reached
              </p>
              <p className="text-xs text-muted-foreground">
                Need additional <span className="font-bold text-yellow-400">
                  {formatCurrency(revenueNeededToBreakEven)}
                </span> in revenue to break even
              </p>
            </div>
          )}

          {/* Decision Insight */}
          <div className={`p-4 rounded-lg border ${
            shouldConsider 
              ? 'bg-green-500/10 border-green-500/50' 
              : isWorthIt 
                ? 'bg-yellow-500/10 border-yellow-500/50'
                : 'bg-red-500/10 border-red-500/50'
          }`}>
            <p className="font-semibold mb-2">
              {shouldConsider 
                ? '✅ Highly Recommended Convention'
                : isWorthIt 
                  ? '⚠️ Marginally Profitable'
                  : '❌ Not Recommended'
              }
            </p>
            <p className="text-sm text-muted-foreground">
              {shouldConsider 
                ? `Excellent ROI of ${formatPercent(roi)}. This convention delivers strong returns and should be prioritized for future attendance.`
                : isWorthIt
                  ? `Profitable but with modest ${formatPercent(roi)} return. Consider if non-financial benefits (networking, brand exposure) add value.`
                  : totalInvestment === 0
                    ? 'Add booth cost and expenses to calculate ROI for this event.'
                    : `Lost ${formatCurrency(Math.abs(netProfit))} on this event. Evaluate if it\'s worth attending next year or if changes can improve profitability.`
              }
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Comparison with Projected */}
      {projectedRevenue > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader>
            <CardTitle className="font-heading">Projected vs Actual</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Projected Revenue:</span>
                <span className="font-mono font-bold">{formatCurrency(projectedRevenue)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Actual Revenue:</span>
                <span className="font-mono font-bold text-pink-500">{formatCurrency(actualRevenue)}</span>
              </div>
              <div className="flex justify-between items-center pt-2 border-t border-border">
                <span className="font-semibold">Variance:</span>
                <span className={`font-mono font-bold ${
                  revenueVariance >= 0 ? 'text-green-400' : 'text-red-400'
                }`}>
                  {actualRevenue >= projectedRevenue ? '+' : ''}{formatCurrency(actualRevenue - projectedRevenue)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
